<template>
  <DataTable
    :value="tableData"
    class="p-datatable-sm"
    :loading="isLoading"
    :paginator="true"
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20]"
    dataKey="_id"
    scrollable
  >
    <template #header>
      <div class="table-title-row flex align-items-center justify-content-center relative">
        <span class="table-title Body-S-Bold">All Discount Codes</span>
      </div>
    </template>

    <Column field="code" header="Code" style="min-width: 120px">
      <template #body="{ data }">
        <span class="discount-code">{{ data.code }}</span>
      </template>
    </Column>

    <Column field="discountType" header="Type" style="min-width: 100px">
      <template #body="{ data }">
        <Tag
          :value="data.discountType === 'percentage' ? 'Percentage' : 'Fixed'"
          :severity="data.discountType === 'percentage' ? 'info' : 'warning'"
          style="font-size: 11px;"
        />
      </template>
    </Column>

    <Column field="value" header="Value" style="min-width: 100px">
      <template #body="{ data }">
        <span class="font-semibold">
          {{ data.discountType === 'percentage' ? `${data.value}%` : `LKR ${Number(data.value).toFixed(2)}` }}
        </span>
      </template>
    </Column>

    <Column field="appliesTo" header="Applies To" style="min-width: 150px">
      <template #body="{ data }">
        <div class="flex gap-1 flex-wrap">
          <Tag
            v-for="role in data.appliesTo"
            :key="role"
            :value="capitalize(role)"
            severity="secondary"
            style="font-size: 10px;"
          />
        </div>
      </template>
    </Column>

    <Column field="duration" header="Duration" style="min-width: 120px">
      <template #body="{ data }">
        <span class="capitalize">
          {{ data.duration === 'repeating' ? `Repeating (${data.monthsDuration}mo)` : capitalize(data.duration) }}
        </span>
      </template>
    </Column>

    <Column field="usedCount" header="Usage" style="min-width: 100px">
      <template #body="{ data }">
        <span>
          {{ data.usedCount }}
          <span class="text-color-secondary">/ {{ data.usageLimit ?? '∞' }}</span>
        </span>
      </template>
    </Column>

    <Column field="expiryDate" header="Expires" style="min-width: 120px">
      <template #body="{ data }">
        <span :class="isExpired(data.expiryDate) ? 'text-red-500' : ''">
          {{ data.expiryDate ? formatDate(data.expiryDate) : '—' }}
        </span>
      </template>
    </Column>

    <Column field="isActive" header="Status" style="min-width: 100px">
      <template #body="{ data }">
        <Tag
          :value="data.isActive ? 'Active' : 'Inactive'"
          :severity="data.isActive ? 'success' : 'danger'"
          style="font-size: 11px;"
        />
      </template>
    </Column>

    <Column header="" style="width: 4rem; min-width: 4rem;">
      <template #body="{ data }">
        <div class="flex justify-content-end">
          <Button
            type="button"
            icon="pi pi-ellipsis-v"
            class="p-button-text p-button-rounded p-button-plain"
            @click="toggleMenu($event, data)"
          />
          <Menu ref="menu" :model="menuItems" :popup="true">
            <template #item="{ item, props }">
              <a v-bind="props.action" :class="item.class">
                <span v-bind="props.label">{{ item.label }}</span>
              </a>
            </template>
          </Menu>
        </div>
      </template>
    </Column>

    <template #paginatorstart>
      <div class="text-sm Caption">
        Showing {{ tableData.length }} of {{ totalDiscounts }} codes
      </div>
    </template>

    <template #empty>
      <div class="flex align-items-center justify-content-center" style="min-height: calc(100vh - 600px)">
        <div class="text-center">
          <i class="pi pi-tag" style="font-size: 2rem; color: var(--surface-400)"></i>
          <p class="mt-2 text-color-secondary">No discount codes found</p>
        </div>
      </div>
    </template>
  </DataTable>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tag from "primevue/tag";
import Button from "primevue/button";
import Menu from "primevue/menu";

defineProps({
  tableData: { type: Array, default: () => [] },
  isLoading: Boolean,
  totalDiscounts: Number,
});

const emit = defineEmits(["edit", "toggle"]);

const menu = ref(null);
const selectedRow = ref(null);

const menuItems = ref([
  {
    label: "Edit",
    command: () => emit("edit", selectedRow.value),
  },
  {
    label: "Toggle Status",
    command: () => emit("toggle", selectedRow.value),
  },
]);

const toggleMenu = (event, rowData) => {
  selectedRow.value = rowData;
  menu.value.toggle(event);
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });

const isExpired = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};
</script>

<style scoped>
.table-title-row {
  padding: 16px 20px;
  text-align: center;
}

.table-title {
  font-weight: 500;
}

.discount-code {
  font-family: monospace;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: var(--surface-100);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 13px;
}

.p-button-text.p-button-rounded.p-button-plain {
  color: var(--black-200);
}
</style>
